/* Examens sur table (prof) — liste des sessions + création (bloc 08-A). */

import type { ComponentType } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowsClockwise,
  CalendarBlank,
  CaretRight,
  CheckCircle,
  ClipboardText,
  FileText,
  PencilSimpleLine,
  Plus,
  UploadSimple,
} from '@phosphor-icons/react';
import { useNavigate } from '../lib/routing';
import ExamImportModal from '../components/exam-import-modal';
import CreateExamSessionModal from '../components/create-exam-session-modal';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { useExamImportJobs } from '../hooks/use-exam-import-jobs';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface Session {
  id: string;
  status: string;
  scheduled_at: string | null;
  exam_title: string;
  classroom_name: string;
  student_count: number;
  copies_collected: number;
}

interface OrphanDocument {
  id: string;
  title: string;
}

type SessionStatus = 'draft' | 'collecting' | 'correcting' | 'done';
type PhosphorIcon = ComponentType<{ size?: number; weight?: 'regular' | 'bold' | 'light' | 'fill'; style?: React.CSSProperties }>;

const STATUS_CONFIG: Record<SessionStatus, { label: string; Icon: PhosphorIcon; active: boolean; badgeClass: string }> = {
  draft: {
    label: 'Brouillon',
    Icon: FileText,
    active: false,
    badgeClass: 'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-muted text-muted-foreground',
  },
  collecting: {
    label: 'Collecte',
    Icon: ArrowsClockwise,
    active: true,
    badgeClass: 'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-accent text-primary',
  },
  correcting: {
    label: 'Correction',
    Icon: PencilSimpleLine,
    active: true,
    badgeClass: 'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-accent text-primary',
  },
  done: {
    label: 'Terminé',
    Icon: CheckCircle,
    active: false,
    badgeClass: 'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-muted text-muted-foreground',
  },
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

export default function Examens() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [orphans, setOrphans] = useState<OrphanDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const { notice, sessionCreatedTick } = useExamImportJobs();

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([api.exam.list.query(), api.exam.listOrphanDocuments.query()])
      .then(([sessionRows, orphanRows]) => {
        setSessions(sessionRows as Session[]);
        setOrphans(orphanRows as OrphanDocument[]);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => reload(), [reload]);

  /* NAI-218 (point 4b) : une session créée depuis cette liste OU depuis la
     page de revue (/teacher/exams/import/:id) doit faire quitter le document
     de « Sans session » sans refresh manuel, même si cette liste est montée
     sur un autre onglet au moment de la création — signal global, même
     mécanisme que `notice` ci-dessous. Déduplication par tick, pas par
     valeur : contrairement à `notice` (un documentId), deux créations
     différentes n'ont ici rien à comparer entre elles. */
  const handledSessionCreatedTickRef = useRef(0);
  useEffect(() => {
    if (sessionCreatedTick === handledSessionCreatedTickRef.current) return;
    handledSessionCreatedTickRef.current = sessionCreatedTick;
    reload();
  }, [sessionCreatedTick, reload]);

  /* NAI-218 : un import peut finir en arrière-plan pendant que l'utilisateur
     est déjà sur cette liste, ou y revient après coup — rien d'autre ne la
     recharge dans ce cas (le flux modal classique navigue directement vers
     l'écran de revue). `notice` est le seul signal global de fin d'import ;
     une ref déduplique pour ne recharger qu'une fois par notice, jamais à
     chaque render tant qu'elle n'a pas changé. */
  const handledNoticeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!notice || notice.status !== 'succeeded') return;
    if (handledNoticeRef.current === notice.documentId) return;
    handledNoticeRef.current = notice.documentId;
    reload();
  }, [notice, reload]);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Header />
      <HeaderContent
        width="md"
        showBurger
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setImporting(true)}>
              <UploadSimple size={16} weight="bold" />
              Importer
            </Button>
            <Button onClick={() => setCreating(true)}>
              <Plus size={16} weight="bold" />
              Nouvelle session
            </Button>
          </div>
        }
      >
        <h1 className={HEADER_TITLE_CLASS}>Examens sur table</h1>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
        {error && <Alert>{error}</Alert>}

        {loading ? (
          <div className="flex flex-col gap-[10px]">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[70px] rounded-card" />)}
          </div>
        ) : error ? (
          <div className="text-center py-12 px-4">
            <p className="text-[14px] text-[var(--naia-text-secondary)] mt-0 mb-4 max-w-[320px] mx-auto">
              {`Vos sessions d'examen n'ont pas pu être chargées. Elles sont intactes.`}
            </p>
            <Button onClick={reload}>
              <ArrowsClockwise size={16} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <details open className="group">
              <summary className="flex items-center gap-2 mb-[10px] cursor-pointer list-none font-extrabold text-[15px] text-[var(--naia-text-primary)] [&::-webkit-details-marker]:hidden">
                <CaretRight
                  size={14}
                  weight="bold"
                  className="shrink-0 text-[var(--naia-text-secondary)] transition-transform group-open:rotate-90 motion-reduce:transition-none"
                  aria-hidden
                />
                {`Avec session (${sessions.length})`}
              </summary>
              {sessions.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div
                    className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                    style={{ background: 'var(--naia-card-alt)', color: 'var(--naia-text-secondary)' }}
                  >
                    <ClipboardText size={28} weight="fill" />
                  </div>
                  <p className="font-extrabold text-[var(--naia-text-primary)] mt-0 mb-1">
                    {`Aucune session d'examen`}
                  </p>
                  <p className="text-[14px] text-[var(--naia-text-secondary)] mt-0 mb-0 max-w-[280px] mx-auto">
                    {`Créez une session à partir d'un document « Évaluation ».`}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-[10px]">
                  {sessions.map((s) => {
                    const cfg = STATUS_CONFIG[s.status as SessionStatus] ?? STATUS_CONFIG.draft;
                    const { Icon } = cfg;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        className="flex items-center gap-3 px-4 py-[14px] bg-card border-[1.5px] border-border rounded-card text-left cursor-pointer w-full font-[inherit] hover:shadow-card transition-shadow"
                        onClick={() => navigate(`/teacher/exams/${s.id}`)}
                      >
                        <div
                          className="shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center"
                          style={{
                            background: cfg.active ? 'var(--naia-info-bg)' : 'var(--naia-card-alt)',
                            color: cfg.active ? 'var(--primary)' : 'var(--naia-text-secondary)',
                          }}
                        >
                          <Icon size={18} weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-[var(--naia-text-primary)] leading-snug truncate">
                            {s.exam_title}
                          </div>
                          <div className="text-[13px] text-[var(--naia-text-secondary)] mt-0.5 flex items-center gap-x-1.5 gap-y-1 flex-wrap">
                            <span className="truncate max-w-full">{s.classroom_name}</span>
                            {s.scheduled_at && (
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                <span aria-hidden>·</span>
                                <CalendarBlank size={12} weight="fill" />
                                {fmtDate(s.scheduled_at)}
                              </span>
                            )}
                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                              <span aria-hidden>·</span>
                              {s.copies_collected}/{s.student_count} copie{s.student_count > 1 ? 's' : ''}
                            </span>
                            <span className={`${cfg.badgeClass} sm:hidden`}>{cfg.label}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`${cfg.badgeClass} hidden sm:inline-block`}>{cfg.label}</span>
                          <span style={{ color: 'var(--naia-text-secondary)' }}>
                            <CaretRight size={16} weight="regular" />
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </details>

            {orphans.length > 0 && (
              <details open className="group">
                <summary className="flex items-center gap-2 mb-[10px] cursor-pointer list-none font-extrabold text-[15px] text-[var(--naia-text-primary)] [&::-webkit-details-marker]:hidden">
                  <CaretRight
                    size={14}
                    weight="bold"
                    className="shrink-0 text-[var(--naia-text-secondary)] transition-transform group-open:rotate-90 motion-reduce:transition-none"
                    aria-hidden
                  />
                  {`Sans session (${orphans.length})`}
                </summary>
                <div className="flex flex-col gap-[10px]">
                  {orphans.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      className="flex items-center gap-3 px-4 py-[14px] bg-card border-[1.5px] border-border rounded-card text-left cursor-pointer w-full font-[inherit] hover:shadow-card transition-shadow"
                      onClick={() => navigate(`/teacher/exams/import/${doc.id}`)}
                    >
                      <div
                        className="shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center"
                        style={{ background: 'var(--naia-card-alt)', color: 'var(--naia-text-secondary)' }}
                      >
                        <FileText size={18} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-[var(--naia-text-primary)] leading-snug truncate">
                          {doc.title}
                        </div>
                      </div>
                      <span style={{ color: 'var(--naia-text-secondary)' }}>
                        <CaretRight size={16} weight="regular" />
                      </span>
                    </button>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </div>

      {creating && (
        <CreateExamSessionModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate(`/teacher/exams/${id}`);
          }}
        />
      )}

      {importing && (
        <ExamImportModal
          onClose={() => setImporting(false)}
          onImported={(id) => {
            setImporting(false);
            navigate(`/teacher/exams/import/${id}`);
          }}
        />
      )}
    </div>
  );
}
